import { isElectron } from 'utils/react/user-agent';

import { sendIpc } from '../pages/vo/vo-react/ipc/send-ipc';

const CLIENT_UPDATE_DOWNLOAD_PROGRESS_MESSAGE = 'POP_CLIENT_UPDATE_DOWNLOAD_PROGRESS';

type ClientUpdateDownloadProgressMessage = {
  type: typeof CLIENT_UPDATE_DOWNLOAD_PROGRESS_MESSAGE;
  percent: number;
};

const isClientUpdateDownloadProgressMessage = (data: unknown): data is ClientUpdateDownloadProgressMessage =>
  !!data &&
  typeof data === 'object' &&
  'type' in data &&
  'percent' in data &&
  data.type === CLIENT_UPDATE_DOWNLOAD_PROGRESS_MESSAGE &&
  typeof data.percent === 'number';

const setupClientUpdateDownloadProgressForwarding = () => {
  if (!isElectron || !('serviceWorker' in navigator)) return;

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (!isClientUpdateDownloadProgressMessage(event.data)) return;
    void sendIpc('setClientLoadingWindowProgress', event.data.percent);
  });
};

export const setupVitePreloadErrorHandler = () => {
  if (isElectron) return;
  window.addEventListener('vite:preloadError', (event) => {
    // If we've failed to load a module on the website (which doesn't have a
    // service worker active), go ahead and reload the page to get the latest
    // assets (assuming this was a deployment cache invalidation).
    setTimeout(() => {
      window.location.reload();
    }, 0);
  });
};

export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    setupClientUpdateDownloadProgressForwarding();

    void navigator.serviceWorker
      .register(import.meta.env.MODE === 'production' ? '/sw.js' : '/dev-sw.js?dev-sw', {
        type: import.meta.env.MODE === 'production' ? 'classic' : 'module',
      })
      .then((registration) => {
        // Immediately install waiting service workers on the browser. The
        // service worker does not cache the html page, so precaching is mostly
        // just to deploys messing with a users' current app

        if (isElectron) return;
        registration.addEventListener('waiting', () => {
          void installServiceWorkerUpdate();
        });
      });
  }
};

/**
 * Updating a service worker takes care; by default, the browser will leisurely
 * discover updates at its own cadence and queue the service worker for updating
 * once all the pages of a domain have offloaded (note that a reload of a page
 * doesn't count as offloading). The waiting phase can be prematurely ended by
 * asking the service worker to release its control directly (here via the
 * SKIP_WAITING message that workbox is looking for). The new version then is
 * able to take control and when the page is reloaded, the new service worker
 * with the new manifest is serving requests.
 */
// Backstop so we never block the update flow forever if `controllerchange`
// somehow doesn't fire (e.g. the worker was already controlling the page).
const CONTROLLER_CHANGE_TIMEOUT_MS = 30_000;

/**
 * Resolves once a (new) service worker takes control of this page. On desktop
 * the active controller fully determines which precached assets are served, so
 * this is the signal that the new version is genuinely live and a reload will
 * pick it up.
 */
const waitForServiceWorkerToTakeControl = () =>
  new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      navigator.serviceWorker.removeEventListener('controllerchange', finish);
      resolve();
    };
    const timeout = setTimeout(finish, CONTROLLER_CHANGE_TIMEOUT_MS);
    navigator.serviceWorker.addEventListener('controllerchange', finish);
  });

/**
 * Ensures the newest available service worker becomes the active controller.
 *
 * Returns `true` if a new worker was activated, `false` if there was nothing to
 * update (already on the latest worker / no registration). Resolves only once
 * the new worker has taken control — resolving any earlier (e.g. at the
 * `installed` state, as we used to) lets a reload fire while the OLD worker is
 * still the controller, so the desktop keeps serving its previous precache.
 */
export const installServiceWorkerUpdate = async (): Promise<boolean> => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;

    await registration.update();

    // The newest worker shows up as `installing` (still downloading/installing)
    // or `waiting` (already installed, parked behind the active worker).
    const newWorker = registration.installing ?? registration.waiting;
    if (!newWorker) return false;

    // Already the active worker — nothing to skip-wait, just report ready.
    if (newWorker.state === 'activated') return true;

    const skipWaiting = () => newWorker.postMessage({ type: 'SKIP_WAITING' });

    // A `waiting` worker is already `installed`; an `installing` worker may have
    // raced past `installed` before we attached a listener. Handle both so we
    // can't deadlock waiting for a `statechange` that already happened.
    if (newWorker.state === 'installed') {
      skipWaiting();
    } else {
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') skipWaiting();
      });
    }

    await waitForServiceWorkerToTakeControl();
    return true;
  } catch (error) {
    throw new Error(`Error checking for service worker update: ${error}`);
  }
};
